import ArrowOutwardIcon from "@mui/icons-material/ArrowOutward";
import CardGiftcardRoundedIcon from "@mui/icons-material/CardGiftcardRounded";
import ChatBubbleOutlineIcon from "@mui/icons-material/ChatBubbleOutline";
import CheckRoundedIcon from "@mui/icons-material/CheckRounded";
import CloseRoundedIcon from "@mui/icons-material/CloseRounded";
import DeleteOutlineIcon from "@mui/icons-material/DeleteOutline";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import LockOutlinedIcon from "@mui/icons-material/LockOutlined";
import ReplyRoundedIcon from "@mui/icons-material/ReplyRounded";
import SendRoundedIcon from "@mui/icons-material/SendRounded";
import WarningAmberRoundedIcon from "@mui/icons-material/WarningAmberRounded";
import {
  Avatar,
  Box,
  Button,
  Chip,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Stack,
  TextField,
  Typography,
} from "@mui/material";
import { enqueueSnackbar } from "notistack";
import { useEffect, useMemo, useState } from "react";

import { useAuth } from "@/shared/stores/useAuth";
import { SocketNotificationType, type User } from "@/shared";
import { useCurrentTalentProfile } from "@/shared/stores/useProfile";
import { useNotificationStore } from "@/shared/stores/useNotificationStore";
import {
  useAddExpComment,
  useAddExpReaction,
  useAddTip,
  useDeleteExpComment,
  useEditExpComment,
  useGetExpCommentList,
  useGetExpReactionList,
  useGetTipList,
} from "@/modules/card-interaction/api/expreaction.api";
import {
  FAN_WALL_STICKER_CATALOG,
  getGiftEmojiByPrice,
} from "@/cards/demo-card/utils/fanWallConstants";
import { Icons } from "@/shared/assets/icon";
import { useTheme } from "@mui/material/styles";
import { useMediaQuery } from "@mui/material";

// function getNotificationPageId(
//   notification: SocketNotificationData
// ): number | null {
//   const raw =
//     notification.page_id ??
//     notification.main_card_page_id ??
//     (notification as { pageId?: number }).pageId;
//   const parsed = Number(raw);
//   if (Number.isFinite(parsed) && parsed > 0) return parsed;

//   const redirect = String(notification.redirect_to ?? "");
//   const fromPath = redirect.match(/\/pages\/(\d+)/i);
//   if (fromPath?.[1]) return Number(fromPath[1]);

//   return null;
// }

export type CardExpInteractionVariant = "single" | "demo" | "season";

interface CardExpInteractionProps {
  pageId: number;
  darkMode: boolean;
  talentId?: number;
  talentName?: string;
  talentAvatar?: string;
  isSingleCardSealed?: boolean;
  footerMode?: "default" | "modal";
}

type ExpReactionKey = "love" | "heart" | "smiley" | "like" | "congrats";

type RawExpComment = {
  id: number;
  user_id?: number;
  talent_id?: number;
  parent_id?: number | null;
  content?: string;
  text?: string;
  created_at?: string;
  gift_price?: number | null;
  user?: {
    id?: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    profile_image?: string;
  };
  talent?: {
    id?: number;
    username?: string;
    profile_image?: string;
  };
  replies?: RawExpComment[];
};

type FlatExpComment = {
  id: number;
  parentId: number | null;
  content: string;
  createdAt: string;
  userId?: number;
  talentId?: number;
  authorName: string;
  authorAvatar: string;
  isTalent: boolean;
  giftPrice?: number;
};

type RawExpTip = {
  id: number;
  page_id?: number;
  user_id?: number;
  amount?: number;
  created_at?: string;
  user?: {
    id?: number;
    first_name?: string;
    last_name?: string;
    username?: string;
    profile_image?: string | null;
  };
};

type FlatExpTip = {
  id: number;
  amount: number;
  createdAt: string;
  userId: number;
  authorName: string;
  authorAvatar: string;
};

const REACTION_OPTIONS: Array<{ key: ExpReactionKey; emoji: string }> = [
  { key: "love", emoji: "❤️" },
  { key: "heart", emoji: "💖" },
  { key: "smiley", emoji: "😊" },
  { key: "like", emoji: "👍" },
  { key: "congrats", emoji: "🎉" },
];

const COMMENT_GIFT_OPTIONS = FAN_WALL_STICKER_CATALOG.slice(0, 5);

const getCommentContent = (comment: RawExpComment): string =>
  String(comment.content ?? comment.text ?? "").trim();

const getUserAuthorName = (comment: RawExpComment): string =>
  comment.user?.username ||
  `${comment.user?.first_name ?? ""} ${comment.user?.last_name ?? ""}`.trim() ||
  "User";

const normalizeExpComment = (
  comment: RawExpComment,
  talentFallback?: { username?: string; profile_image?: string }
): FlatExpComment | null => {
  const content = getCommentContent(comment);
  if (!content) return null;

  const parentId =
    comment.parent_id === null || comment.parent_id === undefined
      ? null
      : Number(comment.parent_id);
  const isTalentReply = parentId !== null;

  let authorName: string;
  let authorAvatar: string;

  if (isTalentReply || comment.talent_id) {
    authorName = comment.talent?.username || talentFallback?.username || "Talent";
    authorAvatar = comment.talent?.profile_image || talentFallback?.profile_image || "";
  } else {
    authorName = getUserAuthorName(comment);
    authorAvatar = comment.user?.profile_image || "";
  }

  const giftPriceRaw = Number(comment.gift_price ?? 0);
  const giftPrice = Number.isFinite(giftPriceRaw) && giftPriceRaw > 0 ? giftPriceRaw : undefined;

  return {
    id: Number(comment.id),
    parentId,
    content,
    createdAt: comment.created_at || "",
    userId: comment.user_id,
    talentId: comment.talent_id ?? undefined,
    authorName,
    authorAvatar,
    isTalent: isTalentReply || Boolean(comment.talent_id),
    giftPrice,
  };
};

const formatCommentTime = (iso?: string): string => {
  if (!iso) return "";
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return "";
  return date.toLocaleString(undefined, {
    month: "short",
    day: "numeric",
    hour: "numeric",
    minute: "2-digit",
  });
};

const getTipAuthorName = (tip: RawExpTip): string =>
  tip.user?.username ||
  `${tip.user?.first_name ?? ""} ${tip.user?.last_name ?? ""}`.trim() ||
  "Supporter";

const normalizeExpTip = (tip: RawExpTip): FlatExpTip | null => {
  const amount = Number(tip.amount ?? 0);
  if (!tip.id || amount <= 0) return null;

  return {
    id: Number(tip.id),
    amount,
    createdAt: tip.created_at || "",
    userId: Number(tip.user_id ?? tip.user?.id ?? 0),
    authorName: getTipAuthorName(tip),
    authorAvatar: tip.user?.profile_image || "",
  };
};

const deductChipsFromWallet = (userInfo: User, amount: number): User => {
  let remaining = amount;
  const purchase = userInfo.wallet_purchase?.available_chips ?? 0;
  const earning = userInfo.wallet_earning?.available_chips ?? 0;

  const purchaseDeduction = Math.min(purchase, remaining);
  remaining -= purchaseDeduction;
  const earningDeduction = Math.min(earning, remaining);

  return {
    ...userInfo,
    wallet_purchase: {
      id: userInfo.wallet_purchase.id,
      available_chips: purchase - purchaseDeduction,
    },
    wallet_earning: userInfo.wallet_earning
      ? {
          id: userInfo.wallet_earning.id,
          available_chips: earning - earningDeduction,
        }
      : null,
  };
};

function buildCardExpInteractionTheme(darkMode: boolean) {
  return {
    panelBg: darkMode
      ? "linear-gradient(180deg, #141312 0%, #0e0d0c 100%)"
      : "var(--primary-100-light)",
    panelHeaderBg: darkMode ? "rgba(255,255,255,0.025)" : "rgba(255,255,255,0.78)",
    chipBg: darkMode ? "rgba(251,191,36,0.09)" : "rgba(180,83,9,0.06)",
    chipColor: darkMode ? "#e2c06a" : "#92400e",
    borderColor: darkMode ? "rgba(251,191,36,0.14)" : "rgba(120,53,15,0.10)",
    softCardBg: darkMode ? "rgba(255,255,255,0.035)" : "#ffffff",
    textMuted: darkMode ? "rgba(254,243,199,0.52)" : "rgba(92,64,51,0.58)",
    titleGrad: darkMode
      ? "linear-gradient(135deg, #f0dfa0 0%, #c9983a 100%)"
      : "linear-gradient(135deg, #92400e 0%, #b45309 100%)",
    titleColor: darkMode ? "#e2c06a" : "#78350f",
    commentCardBg: darkMode ? "rgba(255,255,255,0.03)" : "rgba(255,255,255,0.92)",
    actionEditColor: darkMode ? "#e2c06a" : "#b45309",
    actionDeleteColor: darkMode ? "#f0a0a0" : "#b91c1c",
    actionReplyColor: darkMode ? "#c9983a" : "#c2410c",
    messageUserBubbleBg: darkMode ? "rgba(255,255,255,0.035)" : "#ffffff",
    messageUserBubbleBorder: darkMode ? "rgba(251,191,36,0.18)" : "rgba(120,53,15,0.09)",
    messageUserHeaderColor: darkMode ? "#f0abda" : "#9d174d",
    messageUserTextColor: darkMode ? "rgba(255,252,250,0.92)" : "rgba(41,37,36,0.90)",
    messageReplyBubbleBg: darkMode ? "rgba(255,255,255,0.03)" : "rgba(254,243,199,0.32)",
    messageReplyBubbleBorder: darkMode ? "rgba(251,191,36,0.20)" : "rgba(217,119,6,0.16)",
    messageAccentHighlight: darkMode ? "#c9983a" : "#b45309",
    messagePinkHighlight: darkMode ? "#f0abda" : "#be185d",
    infoBg: darkMode ? "rgba(251,191,36,0.035)" : "rgba(255,255,255,0.72)",
    infoBorder: darkMode ? "rgba(251,191,36,0.10)" : "rgba(120,53,15,0.07)",
    infoAccent: darkMode ? "#c9983a" : "#b45309",
    composeBg: darkMode ? "rgba(255,255,255,0.03)" : "#ffffff",
    composeBorder: darkMode ? "rgba(251,191,36,0.16)" : "rgba(120,53,15,0.09)",
    composeShadow: darkMode ? "0 10px 36px rgba(0,0,0,0.28)" : "0 8px 28px rgba(120,53,15,0.06)",
    handleColor: darkMode ? "rgba(251,191,36,0.26)" : "rgba(180,83,9,0.16)",
    panelShadow: darkMode ? "0 -16px 52px rgba(0,0,0,0.48)" : "0 -12px 40px rgba(120,53,15,0.08)",
    primaryBtnText: darkMode ? "#1a1408" : "#fffbeb",
    scrollbarThumb: darkMode ? "rgba(251,191,36,0.20)" : "rgba(180,83,9,0.14)",
  };
}

function CardExpInteraction({
  pageId,
  darkMode,
  talentId,
  talentName,
  talentAvatar,
  isSingleCardSealed = false,
  footerMode = "default",
}: CardExpInteractionProps) {
  const { userInfo, setUser } = useAuth();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("sm"));
  const { currentProfile } = useCurrentTalentProfile();
  const viewerId = Number(userInfo?.id ?? 0);
  const normalizeTalentId = (id?: string | number | null) => {
    const parsed = Number(id ?? 0);
    return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
  };
  const viewerTalentId = normalizeTalentId(currentProfile?.id);
  const cardTalentId = normalizeTalentId(talentId);
  const isTalentViewer =
    Boolean(viewerTalentId) && (cardTalentId > 0 ? viewerTalentId === cardTalentId : true);
  const canTip = !isTalentViewer;
  const interactionsLocked = isSingleCardSealed;
  const { currentNotification, clearCurrentNotification } = useNotificationStore();

  const embeddedReactionSx = {
    minWidth: "auto",
    px: 0.85,
    py: 0.45,
    minHeight: 34,
    borderRadius: "10px",
    border: "1px solid",
    borderColor: darkMode ? "rgba(251,191,36,0.16)" : "rgba(120,53,15,0.12)",
    background: darkMode ? "rgba(255,255,255,0.04)" : "rgba(255,255,255,0.72)",
    color: darkMode ? "rgba(254,243,199,0.88)" : "#78350f",
    fontSize: 12.5,
    fontWeight: 600,
    lineHeight: 1,
    textTransform: "none" as const,
    boxShadow: "none",
  };

  const embeddedActionSx = {
    minWidth: "auto",
    px: 1.35,
    py: 0.55,
    minHeight: 36,
    borderRadius: "12px",
    border: "1px solid",
    borderColor: darkMode ? "rgba(251,191,36,0.22)" : "rgba(120,53,15,0.16)",
    background: darkMode
      ? "linear-gradient(180deg, rgba(255,255,255,0.07) 0%, rgba(255,255,255,0.03) 100%)"
      : "linear-gradient(180deg, #ffffff 0%, #faf7f2 100%)",
    color: darkMode ? "#e2c06a" : "#92400e",
    fontSize: 12.5,
    fontWeight: 700,
    letterSpacing: "0.02em",
    textTransform: "none" as const,
    boxShadow: darkMode ? "inset 0 1px 0 rgba(255,255,255,0.06)" : "0 1px 2px rgba(120,53,15,0.06)",
    "&:hover": {
      borderColor: darkMode ? "rgba(251,191,36,0.34)" : "rgba(120,53,15,0.24)",
      background: darkMode
        ? "linear-gradient(180deg, rgba(255,255,255,0.1) 0%, rgba(255,255,255,0.05) 100%)"
        : "linear-gradient(180deg, #ffffff 0%, #f5f0e8 100%)",
    },
  };

  const [commentsOpen, setCommentsOpen] = useState(false);
  const [tipsOpen, setTipsOpen] = useState(false);
  const [selectedTipAmount, setSelectedTipAmount] = useState<number | null>(null);

  const [commentDraft, setCommentDraft] = useState("");
  const [selectedCommentGiftPrice, setSelectedCommentGiftPrice] = useState<number | null>(null);
  const [replyDraftById, setReplyDraftById] = useState<Record<number, string>>({});
  const [editDraftById, setEditDraftById] = useState<Record<number, string>>({});
  const [activeEditId, setActiveEditId] = useState<number | null>(null);
  const [pendingEdit, setPendingEdit] = useState<{
    id: number;
    content: string;
  } | null>(null);
  const [pendingDeleteId, setPendingDeleteId] = useState<number | null>(null);
  const [confirmDeleteId, setConfirmDeleteId] = useState<number | null>(null);

  const {
    panelBg,
    panelHeaderBg,

    chipBg,
    chipColor,
    borderColor,
    softCardBg,
    textMuted,
    titleGrad,
    titleColor,
    commentCardBg,
    actionEditColor,
    actionDeleteColor,
    actionReplyColor,
    messageUserBubbleBg,
    messageUserBubbleBorder,
    messageUserHeaderColor,
    messageUserTextColor,
    messageReplyBubbleBg,
    messageReplyBubbleBorder,
    messageAccentHighlight,
    messagePinkHighlight,
    infoBg,
    infoBorder,
    infoAccent,
    composeBg,
    composeBorder,
    composeShadow,
    handleColor,
    panelShadow,
    primaryBtnText,
    scrollbarThumb,
  } = buildCardExpInteractionTheme(darkMode);

  const textFieldSx = {
    "& .MuiOutlinedInput-root": {
      color: chipColor,
      background: softCardBg,
      borderRadius: 2.5,
      fontSize: 13,
      "& fieldset": { borderColor },
      "&:hover fieldset": { borderColor: actionReplyColor },
      "&.Mui-focused fieldset": {
        borderColor: actionReplyColor,
        borderWidth: 1,
      },
    },
    "& .MuiInputBase-input::placeholder": {
      color: textMuted,
      opacity: 1,
    },
  };

  useEffect(() => {
    setCommentsOpen(false);
    setTipsOpen(false);
  }, [pageId]);

  // Parent only mounts this after the user opens this page, so these
  // GET calls do not run for off-screen pages.
  const { data: reactionData, refetch: refetchReactions } = useGetExpReactionList({
    key: ["exp-reactions", pageId],
    params: { pageId },
    options: { enabled: pageId > 0 },
  });

  const { data: commentData, refetch: refetchComments } = useGetExpCommentList({
    key: ["exp-comments", pageId],
    params: {
      pageId,
      ...(currentProfile?.id ? { headers: { "talent-id": String(currentProfile.id) } } : {}),
    },
    options: { enabled: pageId > 0 },
  });

  const { data: tipData, refetch: refetchTips } = useGetTipList({
    key: ["exp-tips", pageId],
    params: { pageId },
    options: { enabled: pageId > 0 },
  });

  const addReactionMutation = useAddExpReaction({
    key: ["exp-add-reaction", pageId],
    params: { pageId },
    options: {
      onSuccess: async () => {
        await refetchReactions();
        enqueueSnackbar("Reaction added", { variant: "success" });
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message || err?.message || "Failed to add reaction", {
          variant: "error",
        });
      },
    },
  });

  const addCommentMutation = useAddExpComment({
    key: ["exp-add-comment", pageId],
    params: { pageId },
    options: {
      onSuccess: async () => {
        await refetchComments();
        enqueueSnackbar("Message added", { variant: "success" });
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message || err?.message || "Failed to add message", {
          variant: "error",
        });
      },
    },
  });

  const addReplyMutation = useAddExpComment({
    key: ["exp-add-reply", pageId],
    params: {
      pageId,
      ...(viewerTalentId ? { headers: { "talent-id": String(viewerTalentId) } } : {}),
    },
    options: {
      onSuccess: async () => {
        await refetchComments();
        enqueueSnackbar("Reply added", { variant: "success" });
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message || err?.message || "Failed to add reply", {
          variant: "error",
        });
      },
    },
  });

  const editCommentMutation = useEditExpComment({
    key: ["exp-edit-comment", pendingEdit?.id],
    params: { commentId: Number(pendingEdit?.id ?? 0) },
    options: {
      onSuccess: async () => {
        await refetchComments();
        enqueueSnackbar("Message updated", { variant: "success" });
      },
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message || err?.message || "Failed to edit message", {
          variant: "error",
        });
      },
    },
  });

  const deleteCommentMutation = useDeleteExpComment({
    key: ["exp-delete-comment", pendingDeleteId],
    params: {
      commentId: Number(pendingDeleteId ?? 0),
      ...(viewerTalentId ? { headers: { "talent-id": String(viewerTalentId) } } : {}),
    },
    options: {
      onSuccess: async () => {
        await refetchComments();
        enqueueSnackbar("Message deleted", { variant: "success" });
      },
      onError: (err: any) => {
        enqueueSnackbar(
          err?.response?.data?.message || err?.message || "Failed to delete message",
          { variant: "error" }
        );
      },
    },
  });

  const addTipMutation = useAddTip({
    key: ["exp-add-tip", pageId],
    params: { pageId },
    options: {
      onError: (err: any) => {
        enqueueSnackbar(err?.response?.data?.message || err?.message || "Failed to send gift", {
          variant: "error",
        });
      },
    },
  });

  const reactions = useMemo(() => {
    const base: Record<ExpReactionKey, number> = {
      love: 0,
      heart: 0,
      smiley: 0,
      like: 0,
      congrats: 0,
    };
    const raw = (reactionData as any)?.data;
    if (!Array.isArray(raw)) return base;
    raw.forEach((reaction: any) => {
      const type = String(reaction?.type ?? reaction?.reaction ?? reaction?.reaction_type ?? "")
        .toLowerCase()
        .trim() as ExpReactionKey;
      if (!Object.prototype.hasOwnProperty.call(base, type)) return;
      const count = typeof reaction?.count === "number" && reaction.count >= 0 ? reaction.count : 1;
      base[type] += count;
    });
    return base;
  }, [reactionData]);

  const userReaction = useMemo((): ExpReactionKey | null => {
    const raw = (reactionData as any)?.data;
    if (!Array.isArray(raw) || !viewerId) return null;
    const userItem = raw.find((reaction: any) => Number(reaction?.user_id) === viewerId);
    if (!userItem) return null;
    const type = String(userItem?.type ?? "")
      .toLowerCase()
      .trim() as ExpReactionKey;
    const validKeys = REACTION_OPTIONS.map((option) => option.key);
    if (!validKeys.includes(type)) return null;
    return type;
  }, [reactionData, viewerId]);

  const talentFallback = useMemo(
    () => ({
      username: talentName || currentProfile?.username,
      profile_image: talentAvatar || currentProfile?.profile_image,
    }),
    [talentName, talentAvatar, currentProfile?.username, currentProfile?.profile_image]
  );

  const parsedComments = useMemo(() => {
    const raw = (commentData as any)?.data;
    if (!Array.isArray(raw)) return [] as FlatExpComment[];

    const flattened: FlatExpComment[] = [];

    raw.forEach((comment: RawExpComment) => {
      const topLevel = normalizeExpComment(comment, talentFallback);
      if (topLevel) flattened.push(topLevel);

      comment.replies?.forEach((reply) => {
        const normalizedReply = normalizeExpComment(reply, talentFallback);
        if (normalizedReply) flattened.push(normalizedReply);
      });
    });

    return flattened;
  }, [commentData, talentFallback]);

  const topLevelComments = useMemo(
    () => parsedComments.filter((item) => item.parentId === null),
    [parsedComments]
  );

  const parsedTips = useMemo(() => {
    const raw = (tipData as any)?.data;
    if (!Array.isArray(raw)) return [] as FlatExpTip[];
    return raw
      .map((tip: RawExpTip) => normalizeExpTip(tip))
      .filter((tip): tip is FlatExpTip => tip !== null)
      .sort((a, b) => new Date(b.createdAt).getTime() - new Date(a.createdAt).getTime());
  }, [tipData]);

  const repliesByParent = useMemo(() => {
    const map: Record<number, FlatExpComment[]> = {};
    parsedComments
      .filter((item) => item.parentId !== null)
      .forEach((item) => {
        const pid = Number(item.parentId);
        if (!map[pid]) map[pid] = [];
        map[pid].push(item);
      });
    return map;
  }, [parsedComments]);

  const userAlreadyCommented = useMemo(() => {
    if (!viewerId || isTalentViewer) return false;
    return topLevelComments.some((comment) => comment.userId === viewerId);
  }, [topLevelComments, viewerId, isTalentViewer]);

  const myMessage = useMemo(() => {
    if (!viewerId || isTalentViewer) return null;
    return topLevelComments.find((comment) => comment.userId === viewerId) ?? null;
  }, [topLevelComments, viewerId, isTalentViewer]);

  const talentDisplayName = talentName || currentProfile?.username || "Talent";

  const getTalentReply = (commentId: number) => {
    const replies = repliesByParent[commentId] || [];
    return replies.find((reply) => reply.isTalent) ?? null;
  };

  const messageButtonCount = isTalentViewer ? topLevelComments.length : myMessage ? 1 : 0;

  const tipCount = parsedTips.length;
  const totalTipAmount = useMemo(
    () => parsedTips.reduce((sum, tip) => sum + tip.amount, 0),
    [parsedTips]
  );

  const availableChips = useMemo(
    () =>
      (userInfo?.wallet_purchase?.available_chips || 0) +
      (userInfo?.wallet_earning?.available_chips || 0),
    [userInfo]
  );

  const handleReact = async (reaction: ExpReactionKey) => {
    if (interactionsLocked || isTalentViewer || addReactionMutation.isLoading) return;
    await addReactionMutation.update({ type: reaction });
  };

  const handleAddComment = async () => {
    if (interactionsLocked || isTalentViewer || userAlreadyCommented) return;
    const content = commentDraft.trim();
    if (!content) return;

    const giftPrice = selectedCommentGiftPrice ?? 0;
    if (giftPrice > 0 && giftPrice > availableChips) {
      enqueueSnackbar("Insufficient chips for the attached gift.", {
        variant: "error",
      });
      return;
    }

    const payload: {
      content: string;
      parent_id: null;
      gift_price?: number;
    } = { content, parent_id: null };
    if (giftPrice > 0) {
      payload.gift_price = giftPrice;
    }

    try {
      await addCommentMutation.update(payload);
    } catch {
      // onError handler already surfaces the API message.
      return;
    }
    if (giftPrice > 0 && userInfo) {
      setUser(deductChipsFromWallet(userInfo, giftPrice));
    }
    setCommentDraft("");
    setSelectedCommentGiftPrice(null);
  };

  const handleAddTip = async () => {
    if (interactionsLocked || !canTip) return;

    const amount = selectedTipAmount ?? 0;

    if (!Number.isFinite(amount) || amount <= 0) {
      enqueueSnackbar("Please select a gift", {
        variant: "warning",
      });
      return;
    }

    if (amount > availableChips) {
      enqueueSnackbar("Insufficient chips. Please check your wallet.", {
        variant: "error",
      });
      return;
    }

    try {
      await addTipMutation.update({ amount });
      if (userInfo) {
        setUser(deductChipsFromWallet(userInfo, amount));
      }
      await refetchTips();
      setSelectedTipAmount(null);
      const giftEmoji = getGiftEmojiByPrice(amount);
      enqueueSnackbar(`${giftEmoji} Gift of ${amount} chips sent successfully!`, {
        variant: "success",
      });
    } catch (_) {
      // onError handled by mutation
    }
  };

  const openCommentsPanel = () => {
    setTipsOpen(false);
    setCommentsOpen(true);
  };

  const openTipsPanel = () => {
    setCommentsOpen(false);
    setTipsOpen(true);
  };

  const isModalFooter = footerMode === "modal";

  const reactionButtons = (
    <>
      {REACTION_OPTIONS.map((reaction) => {
        const isSelected = userReaction === reaction.key;
        return (
          <Button
            key={reaction.key}
            onClick={() => handleReact(reaction.key)}
            disabled={interactionsLocked}
            sx={{
              ...embeddedReactionSx,
              borderColor: isSelected
                ? darkMode
                  ? "rgba(251,191,36,0.42)"
                  : "rgba(180,83,9,0.36)"
                : embeddedReactionSx.borderColor,
              background: isSelected
                ? darkMode
                  ? "rgba(251,191,36,0.16)"
                  : "rgba(245,158,11,0.14)"
                : embeddedReactionSx.background,
              color: isSelected ? (darkMode ? "#fbbf24" : "#92400e") : embeddedReactionSx.color,
            }}
          >
            <Box component="span" sx={{ fontSize: { xs: 15, sm: 16 }, lineHeight: 1 }}>
              {reaction.emoji}
            </Box>
            <Box component="span" sx={{ ml: 0.35, fontSize: { xs: 11, sm: 13 } }}>
              {reactions[reaction.key] || 0}
            </Box>
          </Button>
        );
      })}
    </>
  );

  const giftButton = (
    <Button
      onClick={openTipsPanel}
      sx={{
        ...embeddedActionSx,
        borderColor: tipsOpen
          ? darkMode
            ? "rgba(251,191,36,0.42)"
            : "rgba(180,83,9,0.36)"
          : embeddedActionSx.borderColor,
        background: tipsOpen
          ? darkMode
            ? "rgba(251,191,36,0.16)"
            : "rgba(245,158,11,0.14)"
          : embeddedActionSx.background,
      }}
      aria-label="Open gifts"
    >
      <CardGiftcardRoundedIcon
        sx={{
          fontSize: { xs: 18, sm: 16 },
          mr: { xs: 0, sm: 0.5 },
        }}
      />
      <Box
        component="span"
        sx={{
          display: { xs: tipCount > 0 ? "inline" : "none", sm: "inline" },
        }}
      >
        {tipCount > 0 ? tipCount : "Gift"}
      </Box>
    </Button>
  );

  const commentButton = (
    <Button
      onClick={openCommentsPanel}
      sx={{
        ...embeddedActionSx,
        borderColor: commentsOpen
          ? darkMode
            ? "rgba(251,191,36,0.42)"
            : "rgba(180,83,9,0.36)"
          : embeddedActionSx.borderColor,
        background: commentsOpen
          ? darkMode
            ? "rgba(251,191,36,0.16)"
            : "rgba(245,158,11,0.14)"
          : embeddedActionSx.background,
      }}
      aria-label="Open messages"
    >
      <ChatBubbleOutlineIcon
        sx={{
          fontSize: { xs: 18, sm: 16 },
          mr: { xs: 0, sm: 0.5 },
        }}
      />
      <Box
        component="span"
        sx={{
          display: { xs: messageButtonCount > 0 ? "inline" : "none", sm: "inline" },
        }}
      >
        {messageButtonCount}
      </Box>
    </Button>
  );

  const handleReply = async (parentId: number) => {
    if (interactionsLocked || !isTalentViewer || !viewerTalentId) return;
    const content = (replyDraftById[parentId] || "").trim();
    if (!content) return;
    try {
      await addReplyMutation.update({ content, parent_id: parentId });
    } catch {
      // onError handler already surfaces the API message.
      return;
    }
    setReplyDraftById((prev) => ({ ...prev, [parentId]: "" }));
  };

  const canEdit = (comment: FlatExpComment): boolean => {
    if (interactionsLocked) return false;
    if (isTalentViewer) {
      return comment.isTalent;
    }
    return !comment.isTalent && Boolean(viewerId && comment.userId === viewerId);
  };

  const canDelete = (comment: FlatExpComment): boolean => {
    if (interactionsLocked) return false;
    if (isTalentViewer) {
      if (comment.isTalent) return true;
      return comment.parentId === null;
    }
    return !comment.isTalent && Boolean(viewerId && comment.userId === viewerId);
  };

  const startEdit = (comment: FlatExpComment) => {
    if (interactionsLocked) return;
    setActiveEditId(comment.id);
    setEditDraftById((prev) => ({ ...prev, [comment.id]: comment.content }));
  };

  const cancelEdit = (commentId: number) => {
    setActiveEditId(null);
    setEditDraftById((prev) => {
      const next = { ...prev };
      delete next[commentId];
      return next;
    });
  };

  const handleEditSave = (commentId: number) => {
    if (interactionsLocked) return;
    const draft = (editDraftById[commentId] ?? "").trim();
    if (!draft) return;
    setPendingEdit({ id: commentId, content: draft });
  };

  const handleDelete = (commentId: number) => {
    if (interactionsLocked) return;
    setConfirmDeleteId(commentId);
  };

  const handleConfirmDelete = () => {
    if (interactionsLocked || !confirmDeleteId) return;
    setPendingDeleteId(confirmDeleteId);
  };

  const isDeletingComment = deleteCommentMutation.isLoading;

  useEffect(() => {
    if (!interactionsLocked) return;
    setActiveEditId(null);
    setCommentDraft("");
    setSelectedCommentGiftPrice(null);
    setReplyDraftById({});
    setSelectedTipAmount(null);
    setConfirmDeleteId(null);
  }, [interactionsLocked]);

  useEffect(() => {
    const type = currentNotification?.notification_type;
    if (!type) return;

    if (
      type === SocketNotificationType.MainCardPageComment ||
      type === SocketNotificationType.MainCardPageCommentReplied
    ) {
      void refetchComments();
      clearCurrentNotification();
      return;
    }

    if (type === SocketNotificationType.MainCardPageReaction) {
      void refetchReactions();
      clearCurrentNotification();
      return;
    }

    if (type === SocketNotificationType.MainCardPageTip) {
      void refetchTips();
      clearCurrentNotification();
    }
  }, [
    currentNotification?.notification_type,
    refetchComments,
    refetchReactions,
    refetchTips,
    clearCurrentNotification,
  ]);

  const renderEditField = (commentId: number) => (
    <Stack spacing={1} sx={{ mt: 0.75 }}>
      <TextField
        fullWidth
        multiline
        minRows={2}
        size="small"
        value={editDraftById[commentId] ?? ""}
        onChange={(e) => setEditDraftById((prev) => ({ ...prev, [commentId]: e.target.value }))}
        sx={textFieldSx}
      />
      <Stack direction="row" spacing={1}>
        <Button
          size="small"
          onClick={() => handleEditSave(commentId)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 999,
            px: 2,
            color: primaryBtnText,
            background: titleGrad,
            "&:hover": { opacity: 0.92 },
          }}
        >
          Save
        </Button>
        <Button
          size="small"
          onClick={() => cancelEdit(commentId)}
          sx={{
            textTransform: "none",
            fontWeight: 700,
            borderRadius: 999,
            px: 2,
            color: chipColor,
            border: "1px solid",
            borderColor,
          }}
        >
          Cancel
        </Button>
      </Stack>
    </Stack>
  );

  const renderMessageThread = (comment: FlatExpComment, isOwnMessage = false) => {
    const reply = getTalentReply(comment.id);
    const talentReplied = Boolean(reply);
    const bubbleMaxWidth = { xs: "90%", sm: "78%" };
    const leftBubbleSx = {
      alignSelf: "flex-start",
      maxWidth: bubbleMaxWidth,
      width: "fit-content",
      minWidth: { xs: "72%", sm: 200 },
      mr: "auto",
    };
    const rightBubbleSx = {
      alignSelf: "flex-end",
      maxWidth: bubbleMaxWidth,
      width: "fit-content",
      minWidth: { xs: "72%", sm: 200 },
      ml: "auto",
    };
    const fanMessageSx = isTalentViewer ? leftBubbleSx : rightBubbleSx;
    const talentReplySx = isTalentViewer ? rightBubbleSx : leftBubbleSx;

    return (
      <Box key={comment.id} sx={{ width: "100%" }}>
        <Stack spacing={1.5} sx={{ width: "100%", alignItems: "stretch" }}>
          <Box
            sx={{
              ...fanMessageSx,
              p: 1.5,
              borderRadius: 2.5,
              border: "1px solid",
              borderColor: messageUserBubbleBorder,
              background: messageUserBubbleBg,
            }}
          >
            <Typography
              sx={{
                fontWeight: 800,
                fontSize: 12.5,
                color: messageUserHeaderColor,
                mb: 0.75,
              }}
            >
              {isOwnMessage ? (
                <>
                  me
                  {comment.giftPrice ? (
                    <>
                      {" · "}
                      <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                        {getGiftEmojiByPrice(comment.giftPrice)}
                      </Box>
                      {" gift attached"}
                    </>
                  ) : null}
                </>
              ) : (
                <>
                  {comment.authorName}
                  {comment.giftPrice ? (
                    <>
                      {" · "}
                      <Box component="span" sx={{ fontSize: 16, lineHeight: 1 }}>
                        {getGiftEmojiByPrice(comment.giftPrice)}
                      </Box>
                      {" gift attached"}
                    </>
                  ) : null}
                </>
              )}
            </Typography>

            {activeEditId === comment.id && !interactionsLocked ? (
              renderEditField(comment.id)
            ) : (
              <Typography
                sx={{
                  fontSize: 14,
                  color: messageUserTextColor,
                  lineHeight: 1.55,
                  whiteSpace: "pre-wrap",
                  wordBreak: "break-word",
                }}
              >
                {comment.content}
              </Typography>
            )}

            <Stack
              direction="row"
              alignItems="center"
              justifyContent="space-between"
              flexWrap="wrap"
              gap={0.5}
              sx={{ mt: 1.25 }}
            >
              {isOwnMessage ? (
                <Stack direction="row" alignItems="center" spacing={0.5}>
                  <ArrowOutwardIcon sx={{ fontSize: 13, color: textMuted, opacity: 0.8 }} />
                  <Typography sx={{ fontSize: 11.5, color: textMuted }}>
                    showing on the Fan Wall
                  </Typography>
                </Stack>
              ) : (
                comment.createdAt && (
                  <Typography sx={{ fontSize: 11.5, color: textMuted }}>
                    {formatCommentTime(comment.createdAt)}
                  </Typography>
                )
              )}
              {activeEditId !== comment.id && (
                <Stack direction="row" spacing={0.5} alignItems="center">
                  {canEdit(comment) && (
                    <Button
                      size="small"
                      startIcon={<EditOutlinedIcon sx={{ fontSize: 14 }} />}
                      onClick={() => startEdit(comment)}
                      sx={{
                        minWidth: "auto",
                        px: 0.75,
                        py: 0.25,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12,
                        color: actionEditColor,
                      }}
                    >
                      Edit
                    </Button>
                  )}
                  {canDelete(comment) && (
                    <Button
                      size="small"
                      startIcon={<DeleteOutlineIcon sx={{ fontSize: 14 }} />}
                      onClick={() => handleDelete(comment.id)}
                      sx={{
                        minWidth: "auto",
                        px: 0.75,
                        py: 0.25,
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12,
                        color: actionDeleteColor,
                      }}
                    >
                      Delete
                    </Button>
                  )}
                </Stack>
              )}
            </Stack>
          </Box>

          {reply && (
            <Box
              sx={{
                ...talentReplySx,
                p: 1.5,
                borderRadius: 2.5,
                border: "1px solid",
                borderColor: messageReplyBubbleBorder,
                background: messageReplyBubbleBg,
              }}
            >
              <Stack
                direction="row"
                alignItems="center"
                spacing={1}
                flexWrap="wrap"
                sx={{ mb: 0.75 }}
              >
                <Avatar
                  src={reply.authorAvatar || talentAvatar || currentProfile?.profile_image || ""}
                  sx={{
                    width: 28,
                    height: 28,
                    border: `1px solid ${messageReplyBubbleBorder}`,
                  }}
                />
                <Typography
                  sx={{
                    fontWeight: 800,
                    fontSize: 13.5,
                    color: chipColor,
                  }}
                >
                  {reply.authorName || talentDisplayName}
                </Typography>
                <Chip
                  icon={<CheckRoundedIcon sx={{ fontSize: "14px !important" }} />}
                  label={isOwnMessage ? "to you" : "private"}
                  size="small"
                  sx={{
                    height: 22,
                    fontSize: 11,
                    fontWeight: 800,
                    background: chipBg,
                    color: messageAccentHighlight,
                    border: `1px solid ${messageReplyBubbleBorder}`,
                    "& .MuiChip-icon": { color: messageAccentHighlight },
                  }}
                />
                {activeEditId !== reply.id && (canEdit(reply) || canDelete(reply)) && (
                  <Stack direction="row" spacing={0.25} alignItems="center" sx={{ ml: "auto" }}>
                    {canEdit(reply) && (
                      <IconButton
                        size="small"
                        onClick={() => startEdit(reply)}
                        sx={{ color: actionEditColor, p: 0.5 }}
                      >
                        <EditOutlinedIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                    {canDelete(reply) && (
                      <IconButton
                        size="small"
                        onClick={() => handleDelete(reply.id)}
                        sx={{ color: actionDeleteColor, p: 0.5 }}
                      >
                        <DeleteOutlineIcon sx={{ fontSize: 16 }} />
                      </IconButton>
                    )}
                  </Stack>
                )}
              </Stack>

              {activeEditId === reply.id && !interactionsLocked ? (
                renderEditField(reply.id)
              ) : (
                <Typography
                  sx={{
                    fontSize: 14,
                    color: darkMode ? "rgba(254,243,199,0.90)" : "rgba(120,53,15,0.90)",
                    lineHeight: 1.55,
                    whiteSpace: "pre-wrap",
                    wordBreak: "break-word",
                  }}
                >
                  {reply.content}
                </Typography>
              )}

              <Stack
                direction="row"
                alignItems="center"
                spacing={0.5}
                sx={{
                  mt: 1.25,
                  pt: 1,
                  borderTop: "1px solid",
                  borderColor: messageReplyBubbleBorder,
                }}
              >
                <LockOutlinedIcon sx={{ fontSize: 13, color: textMuted }} />
                <Typography sx={{ fontSize: 11.5, color: textMuted }}>
                  This reply is private — it never appears on the Wall.
                </Typography>
              </Stack>
            </Box>
          )}

          {isTalentViewer && !talentReplied && !interactionsLocked && (
            <Box sx={talentReplySx}>
              <Stack
                direction="row"
                spacing={1}
                alignItems="flex-end"
                sx={{
                  p: 1.25,
                  borderRadius: 2,
                  border: "1px dashed",
                  borderColor: messageReplyBubbleBorder,
                  background: softCardBg,
                }}
              >
                <Avatar
                  src={talentAvatar || currentProfile?.profile_image || ""}
                  sx={{
                    width: 30,
                    height: 30,
                    mb: 0.25,
                    border: `1px solid ${messageReplyBubbleBorder}`,
                    flexShrink: 0,
                  }}
                />
                <TextField
                  fullWidth
                  multiline
                  minRows={1}
                  maxRows={3}
                  size="small"
                  placeholder={`Write a private reply to ${comment.authorName}...`}
                  value={replyDraftById[comment.id] || ""}
                  onChange={(e) =>
                    setReplyDraftById((prev) => ({
                      ...prev,
                      [comment.id]: e.target.value,
                    }))
                  }
                  onKeyDown={(e) => {
                    if (e.key === "Enter" && !e.shiftKey) {
                      e.preventDefault();
                      void handleReply(comment.id);
                    }
                  }}
                  sx={textFieldSx}
                />
                <Button
                  size="small"
                  startIcon={<ReplyRoundedIcon sx={{ fontSize: 16 }} />}
                  onClick={() => void handleReply(comment.id)}
                  disabled={!(replyDraftById[comment.id] || "").trim()}
                  sx={{
                    textTransform: "none",
                    fontWeight: 700,
                    borderRadius: 999,
                    px: 1.75,
                    whiteSpace: "nowrap",
                    flexShrink: 0,
                    color: darkMode ? "#78350f" : "#fffbeb",
                    background: titleGrad,
                    opacity: (replyDraftById[comment.id] || "").trim() ? 1 : 0.55,
                    "&:hover": { opacity: 0.92 },
                  }}
                >
                  Reply
                </Button>
              </Stack>
            </Box>
          )}
        </Stack>
      </Box>
    );
  };

  useEffect(() => {
    if (!pendingEdit?.id || !pendingEdit.content) return;

    let cancelled = false;
    const run = async () => {
      try {
        await editCommentMutation.update({ content: pendingEdit.content });
        if (cancelled) return;
        await refetchComments();
        setActiveEditId(null);
        setEditDraftById((prev) => {
          const next = { ...prev };
          delete next[pendingEdit.id];
          return next;
        });
        enqueueSnackbar("Message updated", { variant: "success" });
      } catch (_) {
        // onError handled by mutation
      } finally {
        if (!cancelled) setPendingEdit(null);
      }
    };
    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingEdit]);

  useEffect(() => {
    if (!pendingDeleteId) return;

    let cancelled = false;
    const run = async () => {
      try {
        await deleteCommentMutation.update({});
        if (cancelled) return;
        await refetchComments();
        enqueueSnackbar("Message deleted", { variant: "success" });
      } catch (_) {
        // onError handled by mutation
      } finally {
        if (!cancelled) {
          setPendingDeleteId(null);
          setConfirmDeleteId(null);
        }
      }
    };
    run();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [pendingDeleteId]);

  return (
    <>
      <Box
        sx={{
          position: "sticky",
          bottom: isMobile ? 28 : 0,
          zIndex: 20,
          display: "flex",
          flexDirection: "column",
          flexShrink: 0,
        }}
      >
        <Box
          sx={{
            flexShrink: 0,
            maxHeight: commentsOpen ? "72vh" : 0,
            opacity: commentsOpen ? 1 : 0,
            transform: commentsOpen ? "translateY(0)" : "translateY(20px)",
            transition: "max-height 0.32s ease, opacity 0.22s ease, transform 0.32s ease",
            overflow: "hidden",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            background: panelBg,
            borderTop: commentsOpen ? "1px solid" : "0px solid",
            borderColor,
            boxShadow: commentsOpen ? panelShadow : "none",
          }}
        >
          {/* Header */}
          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              pt: 1.5,
              pb: 1.75,
              borderBottom: "1px solid",
              borderColor,
              background: panelHeaderBg,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 4,
                borderRadius: 999,
                background: handleColor,
                mx: "auto",
                mb: 1.5,
              }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <ChatBubbleOutlineIcon sx={{ color: chipColor, fontSize: 18, opacity: 0.9 }} />
                <Typography
                  sx={{
                    fontFamily: '"Lexend", sans-serif',
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    letterSpacing: "-0.01em",
                    color: titleColor,
                  }}
                >
                  {isTalentViewer ? "Fan messages" : "My message"}
                </Typography>
                {isTalentViewer && (
                  <Chip
                    label={topLevelComments.length}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 800,
                      fontSize: 11,
                      background: chipBg,
                      color: chipColor,
                      border: `1px solid ${borderColor}`,
                    }}
                  />
                )}
              </Stack>
              <Button
                onClick={() => setCommentsOpen(false)}
                size="small"
                startIcon={<CloseRoundedIcon sx={{ fontSize: 15 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  color: chipColor,
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.35,
                  minHeight: 32,
                  border: "1px solid",
                  borderColor,
                  background: chipBg,
                  "&:hover": {
                    background: darkMode ? "rgba(251,191,36,0.14)" : "rgba(180,83,9,0.10)",
                  },
                }}
              >
                Close
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              overflowY: "auto",
              maxHeight: "calc(72vh - 72px)",
              display: "flex",
              flexDirection: "column",
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: scrollbarThumb,
                borderRadius: 999,
              },
            }}
          >
            <Box
              sx={{
                mb: 2.5,
                pl: 1.75,
                pr: 1.5,
                py: 1.35,
                borderRadius: "12px",
                border: "1px solid",
                borderColor: infoBorder,
                borderLeft: "3px solid",
                borderLeftColor: infoAccent,
                background: infoBg,
              }}
            >
              <Typography
                sx={{
                  fontSize: 12.5,
                  lineHeight: 1.65,
                  color: darkMode ? "rgba(254,243,199,0.78)" : "rgba(92,64,51,0.82)",
                }}
              >
                {isTalentViewer ? (
                  <>
                    Each fan can leave one message per page. Your replies are{" "}
                    <Box component="span" sx={{ color: messageAccentHighlight, fontWeight: 700 }}>
                      private to each fan
                    </Box>{" "}
                    and stay in their message thread.
                  </>
                ) : (
                  <>
                    One message per page. {talentDisplayName}&apos;s reply is{" "}
                    <Box component="span" sx={{ color: messageAccentHighlight, fontWeight: 700 }}>
                      private, just for you
                    </Box>
                    , and stays here.
                  </>
                )}
              </Typography>
            </Box>

            {!isTalentViewer && !interactionsLocked && !userAlreadyCommented && !myMessage && (
              <Stack
                direction="row"
                spacing={1.25}
                alignItems="flex-start"
                sx={{
                  mb: 2,
                  alignSelf: "flex-end",
                  ml: "auto",
                  maxWidth: { xs: "90%", sm: "78%" },
                  width: "100%",
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor: messageUserBubbleBorder,
                  background: messageUserBubbleBg,
                }}
              >
                <Avatar
                  src={userInfo?.profile_image || ""}
                  sx={{
                    width: 36,
                    height: 36,
                    border: `1.5px solid ${composeBorder}`,
                    boxShadow: darkMode
                      ? "0 2px 8px rgba(0,0,0,0.25)"
                      : "0 2px 8px rgba(120,53,15,0.08)",
                  }}
                />
                <Box sx={{ flex: 1 }}>
                  <TextField
                    fullWidth
                    multiline
                    minRows={2}
                    maxRows={4}
                    size="small"
                    value={commentDraft}
                    onChange={(e) => setCommentDraft(e.target.value)}
                    placeholder="Write your one message for this page..."
                    sx={textFieldSx}
                    onKeyDown={(e) => {
                      if (e.key === "Enter" && !e.shiftKey) {
                        e.preventDefault();
                        void handleAddComment();
                      }
                    }}
                  />
                  <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap" sx={{ mt: 1 }}>
                    {COMMENT_GIFT_OPTIONS.map((sticker: any) => {
                      const isSelected = selectedCommentGiftPrice === sticker.price;
                      const isDisabled = sticker.price > availableChips;
                      return (
                        <Chip
                          key={sticker.type}
                          label={
                            <Stack direction="row" alignItems="center" spacing={0.5}>
                              <Box component="span" sx={{ fontSize: 20, lineHeight: 1 }}>
                                {sticker.emoji}
                              </Box>
                              <Box component="span" sx={{ fontSize: 11, fontWeight: 700 }}>
                                {sticker.price}
                              </Box>
                            </Stack>
                          }
                          clickable={!isDisabled && !addCommentMutation.isLoading}
                          onClick={() => {
                            if (isDisabled || addCommentMutation.isLoading) {
                              return;
                            }
                            setSelectedCommentGiftPrice((prev) =>
                              prev === sticker.price ? null : sticker.price
                            );
                          }}
                          sx={{
                            height: "auto",
                            py: 0.45,
                            px: 0.25,
                            fontWeight: 600,
                            background: isSelected
                              ? darkMode
                                ? "rgba(251,191,36,0.18)"
                                : "rgba(180,83,9,0.12)"
                              : chipBg,
                            color: chipColor,
                            border: `1px solid ${isSelected ? actionReplyColor : borderColor}`,
                            opacity: isDisabled ? 0.42 : 1,
                            transition: "background 180ms ease, border-color 180ms ease",
                          }}
                        />
                      );
                    })}
                  </Stack>
                  <Stack
                    direction="row"
                    justifyContent="space-between"
                    alignItems="center"
                    sx={{ mt: 1 }}
                  >
                    {selectedCommentGiftPrice ? (
                      <Typography sx={{ fontSize: 11.5, color: textMuted }}>
                        Optional gift will be attached to your message
                      </Typography>
                    ) : (
                      <Box />
                    )}
                    <Button
                      size="small"
                      startIcon={<SendRoundedIcon sx={{ fontSize: 15 }} />}
                      onClick={() => void handleAddComment()}
                      disabled={!commentDraft.trim() || addCommentMutation.isLoading}
                      sx={{
                        textTransform: "none",
                        fontWeight: 700,
                        fontSize: 12.5,
                        borderRadius: 999,
                        px: 2.25,
                        py: 0.65,
                        color: primaryBtnText,
                        background: titleGrad,
                        boxShadow: darkMode
                          ? "0 4px 14px rgba(0,0,0,0.28)"
                          : "0 4px 12px rgba(180,83,9,0.18)",
                        opacity: commentDraft.trim() && !addCommentMutation.isLoading ? 1 : 0.5,
                        "&:hover": {
                          opacity:
                            commentDraft.trim() && !addCommentMutation.isLoading ? 0.92 : 0.5,
                        },
                        "&.Mui-disabled": {
                          color: primaryBtnText,
                          background: titleGrad,
                          opacity: 0.38,
                        },
                      }}
                    >
                      {addCommentMutation.isLoading
                        ? "Sending…"
                        : selectedCommentGiftPrice
                          ? `Send with ${getGiftEmojiByPrice(selectedCommentGiftPrice)}`
                          : "Send message"}
                    </Button>
                  </Stack>
                </Box>
              </Stack>
            )}

            <Stack spacing={2.5}>
              {isTalentViewer ? (
                topLevelComments.length === 0 ? (
                  <Box
                    sx={{
                      textAlign: "center",
                      py: 4,
                      px: 2,
                      borderRadius: 2.5,
                      border: "1px dashed",
                      borderColor,
                      background: commentCardBg,
                    }}
                  >
                    <ChatBubbleOutlineIcon
                      sx={{
                        fontSize: 36,
                        color: chipColor,
                        opacity: 0.55,
                        mb: 1,
                      }}
                    />
                    <Typography
                      sx={{
                        color: textMuted,
                        fontSize: 13.5,
                        lineHeight: 1.55,
                        fontStyle: "italic",
                      }}
                    >
                      {interactionsLocked
                        ? "No new messages will appear on this sealed card"
                        : "No fan messages yet. Supporter messages will appear here."}
                    </Typography>
                  </Box>
                ) : (
                  topLevelComments.map((comment) => renderMessageThread(comment))
                )
              ) : !myMessage && (interactionsLocked || userAlreadyCommented) ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    px: 2,
                    borderRadius: 2.5,
                    border: "1px dashed",
                    borderColor,
                    background: commentCardBg,
                  }}
                >
                  <ChatBubbleOutlineIcon
                    sx={{ fontSize: 36, color: chipColor, opacity: 0.55, mb: 1 }}
                  />
                  <Typography
                    sx={{
                      color: textMuted,
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      fontStyle: "italic",
                    }}
                  >
                    {interactionsLocked
                      ? "You can't add a message on this sealed card"
                      : userAlreadyCommented
                        ? "You've already shared your one message on this page."
                        : "No message yet. Share your one message for this page!"}
                  </Typography>
                </Box>
              ) : myMessage ? (
                renderMessageThread(myMessage, true)
              ) : null}
            </Stack>

            {!isTalentViewer && myMessage && (
              <Typography
                sx={{
                  mt: 2,
                  fontSize: 12.5,
                  lineHeight: 1.55,
                  color: textMuted,
                  textAlign: "center",
                }}
              >
                You&apos;ve shared your{" "}
                <Box component="span" sx={{ fontWeight: 800, color: chipColor }}>
                  one message
                </Box>{" "}
                on this page.{" "}
                <Box component="span" sx={{ fontWeight: 700, color: actionEditColor }}>
                  Edit
                </Box>{" "}
                or{" "}
                <Box component="span" sx={{ fontWeight: 700, color: actionDeleteColor }}>
                  delete
                </Box>{" "}
                it anytime.
              </Typography>
            )}
          </Box>
        </Box>

        <Box
          sx={{
            flexShrink: 0,
            maxHeight: tipsOpen ? "72vh" : 0,
            opacity: tipsOpen ? 1 : 0,
            transform: tipsOpen ? "translateY(0)" : "translateY(20px)",
            transition: "max-height 0.32s ease, opacity 0.22s ease, transform 0.32s ease",
            overflow: "hidden",
            borderTopLeftRadius: 20,
            borderTopRightRadius: 20,
            background: panelBg,
            borderTop: tipsOpen ? "1px solid" : "0px solid",
            borderColor,
            boxShadow: tipsOpen ? panelShadow : "none",
          }}
        >
          <Box
            sx={{
              px: { xs: 2, sm: 2.5 },
              pt: 1.5,
              pb: 1.75,
              borderBottom: "1px solid",
              borderColor,
              background: panelHeaderBg,
            }}
          >
            <Box
              sx={{
                width: 36,
                height: 4,
                borderRadius: 999,
                background: handleColor,
                mx: "auto",
                mb: 1.5,
              }}
            />
            <Stack direction="row" alignItems="center" justifyContent="space-between">
              <Stack direction="row" alignItems="center" spacing={1}>
                <CardGiftcardRoundedIcon sx={{ color: chipColor, fontSize: 18, opacity: 0.9 }} />
                <Typography
                  sx={{
                    fontFamily: '"Lexend", sans-serif',
                    fontWeight: 700,
                    fontSize: "0.95rem",
                    letterSpacing: "-0.01em",
                    color: titleColor,
                  }}
                >
                  Gifts{isTalentViewer ? " received" : ""}
                </Typography>
                <Chip
                  label={tipCount}
                  size="small"
                  sx={{
                    height: 22,
                    fontWeight: 800,
                    fontSize: 11,
                    background: chipBg,
                    color: chipColor,
                    border: `1px solid ${borderColor}`,
                  }}
                />
                {totalTipAmount > 0 && (
                  <Chip
                    icon={<Icons.Coin height={12} width={12} />}
                    label={totalTipAmount}
                    size="small"
                    sx={{
                      height: 22,
                      fontWeight: 800,
                      fontSize: 11,
                      background: chipBg,
                      color: chipColor,
                      border: `1px solid ${borderColor}`,
                      "& .MuiChip-icon": { ml: 0.75 },
                    }}
                  />
                )}
              </Stack>
              <Button
                onClick={() => setTipsOpen(false)}
                size="small"
                startIcon={<CloseRoundedIcon sx={{ fontSize: 15 }} />}
                sx={{
                  textTransform: "none",
                  fontWeight: 600,
                  fontSize: 12,
                  color: chipColor,
                  borderRadius: 999,
                  px: 1.5,
                  py: 0.35,
                  minHeight: 32,
                  border: "1px solid",
                  borderColor,
                  background: chipBg,
                  "&:hover": {
                    background: darkMode ? "rgba(251,191,36,0.14)" : "rgba(180,83,9,0.10)",
                  },
                }}
              >
                Close
              </Button>
            </Stack>
          </Box>

          <Box
            sx={{
              p: { xs: 2, sm: 2.5 },
              overflowY: "auto",
              maxHeight: "calc(72vh - 72px)",
              "&::-webkit-scrollbar": { width: 5 },
              "&::-webkit-scrollbar-thumb": {
                backgroundColor: scrollbarThumb,
                borderRadius: 999,
              },
            }}
          >
            {isTalentViewer && (
              <Typography
                sx={{
                  mb: 2,
                  fontSize: 13,
                  lineHeight: 1.55,
                  color: textMuted,
                }}
              >
                Supporters send gifts here. You only receive them.
              </Typography>
            )}
            {canTip && !interactionsLocked && (
              <Stack
                spacing={1.5}
                sx={{
                  mb: 2,
                  p: 1.5,
                  borderRadius: 2.5,
                  border: "1px solid",
                  borderColor,
                  background: commentCardBg,
                }}
              >
                <Stack
                  direction="row"
                  alignItems="center"
                  justifyContent="space-between"
                  spacing={1}
                >
                  <Typography
                    sx={{
                      fontWeight: 800,
                      fontSize: 13.5,
                      color: chipColor,
                      fontFamily: '"Lexend", sans-serif',
                    }}
                  >
                    Send a gift{talentName ? ` to ${talentName}` : ""}
                  </Typography>
                  <Stack direction="row" alignItems="center" spacing={0.5}>
                    <Icons.Coin height={14} width={14} />
                    <Typography sx={{ fontSize: 12, color: textMuted }}>
                      {availableChips} available
                    </Typography>
                  </Stack>
                </Stack>

                <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
                  {FAN_WALL_STICKER_CATALOG.slice(0, 5).map((sticker: any) => {
                    const isSelected = selectedTipAmount === sticker.price;
                    const isDisabled = sticker.price > availableChips;
                    return (
                      <Chip
                        key={sticker.type}
                        label={
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Box component="span" sx={{ fontSize: 22, lineHeight: 1 }}>
                              {sticker.emoji}
                            </Box>
                            <Box component="span" sx={{ fontSize: 12, fontWeight: 700 }}>
                              {sticker.price}
                            </Box>
                          </Stack>
                        }
                        clickable={!isDisabled && !addTipMutation.isLoading}
                        onClick={() => {
                          if (isDisabled || addTipMutation.isLoading) return;
                          setSelectedTipAmount(sticker.price);
                        }}
                        sx={{
                          height: "auto",
                          py: 0.75,
                          fontWeight: 700,
                          background: isSelected
                            ? darkMode
                              ? "rgba(245,158,11,0.35)"
                              : "rgba(245,158,11,0.22)"
                            : chipBg,
                          color: chipColor,
                          border: `1px solid ${isSelected ? actionReplyColor : borderColor}`,
                          opacity: isDisabled ? 0.45 : 1,
                        }}
                      />
                    );
                  })}
                </Stack>

                <Stack direction="row" justifyContent="flex-end">
                  <Button
                    size="small"
                    startIcon={
                      addTipMutation.isLoading ? (
                        <CircularProgress size={14} sx={{ color: "inherit" }} />
                      ) : (
                        <SendRoundedIcon sx={{ fontSize: 16 }} />
                      )
                    }
                    onClick={handleAddTip}
                    disabled={addTipMutation.isLoading || !selectedTipAmount}
                    sx={{
                      textTransform: "none",
                      fontWeight: 700,
                      borderRadius: 999,
                      px: 2,
                      color: "#fffbeb",
                      background: titleGrad,
                      opacity: selectedTipAmount ? 1 : 0.55,
                      "&:hover": { opacity: 0.92 },
                    }}
                  >
                    {addTipMutation.isLoading ? (
                      "Sending…"
                    ) : selectedTipAmount ? (
                      <>
                        Send{" "}
                        <Box component="span" sx={{ fontSize: 18, lineHeight: 1 }}>
                          {getGiftEmojiByPrice(selectedTipAmount)}
                        </Box>{" "}
                        Gift
                      </>
                    ) : (
                      "Send Gift"
                    )}
                  </Button>
                </Stack>
              </Stack>
            )}

            <Stack spacing={1.25}>
              {parsedTips.length === 0 ? (
                <Box
                  sx={{
                    textAlign: "center",
                    py: 4,
                    px: 2,
                    borderRadius: 2.5,
                    border: "1px dashed",
                    borderColor,
                    background: commentCardBg,
                  }}
                >
                  <CardGiftcardRoundedIcon
                    sx={{ fontSize: 36, color: chipColor, opacity: 0.55, mb: 1 }}
                  />
                  <Typography
                    sx={{
                      color: textMuted,
                      fontSize: 13.5,
                      lineHeight: 1.55,
                      fontStyle: "italic",
                    }}
                  >
                    {isTalentViewer
                      ? "No gifts received yet. Gifts from supporters will appear here."
                      : "No gifts yet. Be the first to send a gift!"}
                  </Typography>
                </Box>
              ) : (
                parsedTips.map((tip) => (
                  <Box
                    key={tip.id}
                    sx={{
                      p: 1.5,
                      borderRadius: 2.5,
                      border: "1px solid",
                      borderColor,
                      background: commentCardBg,
                    }}
                  >
                    <Stack direction="row" spacing={1.25} alignItems="center">
                      <Avatar
                        src={tip.authorAvatar}
                        sx={{
                          width: 36,
                          height: 36,
                          border: `1px solid ${borderColor}`,
                        }}
                      />
                      <Box sx={{ flex: 1, minWidth: 0 }}>
                        <Stack
                          direction="row"
                          alignItems="center"
                          justifyContent="space-between"
                          spacing={1}
                        >
                          <Typography
                            sx={{
                              fontWeight: 800,
                              fontSize: 13.5,
                              color: chipColor,
                              fontFamily: '"Lexend", sans-serif',
                            }}
                          >
                            {tip.authorName}
                          </Typography>
                          <Stack direction="row" alignItems="center" spacing={0.5}>
                            <Typography sx={{ fontSize: 24, lineHeight: 1 }}>
                              {getGiftEmojiByPrice(tip.amount)}
                            </Typography>
                            <Icons.Coin height={14} width={14} />
                            <Typography
                              sx={{
                                fontWeight: 800,
                                fontSize: 13.5,
                                color: chipColor,
                              }}
                            >
                              {tip.amount}
                            </Typography>
                          </Stack>
                        </Stack>
                        {tip.createdAt && (
                          <Typography sx={{ fontSize: 11, color: textMuted, mt: 0.25 }}>
                            {formatCommentTime(tip.createdAt)}
                          </Typography>
                        )}
                      </Box>
                    </Stack>
                  </Box>
                ))
              )}
            </Stack>
          </Box>
        </Box>
        <Box
          sx={{
            position: "relative",
            zIndex: 21,
            flexShrink: 0,
            borderTop: "1px solid",
            borderColor,
            px: { xs: 0.75, sm: 2 },
            py: { xs: 0.75, sm: 1 },
            // bgcolor: panelBg,
            // backdropFilter: "blur(14px)",
            pb: isModalFooter ? "max(10px, env(safe-area-inset-bottom, 0px))" : undefined,
          }}
        >
          <Stack
            direction="row"
            alignItems="center"
            justifyContent="space-between"
            sx={{
              width: "100%",
              gap: { xs: 0.5, sm: 0.75 },
              overflowX: { xs: "auto", sm: "visible" },
              overflowY: "hidden",
              flexWrap: "nowrap",
              WebkitOverflowScrolling: "touch",
              scrollbarWidth: "none",
              "&::-webkit-scrollbar": {
                display: "none",
              },
              pb: { xs: 0.15, sm: 0 },
            }}
          >
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                gap: { xs: 0.4, sm: 0.75 },
                flexShrink: 0,
                flexWrap: "nowrap",
              }}
            >
              {reactionButtons}
            </Stack>
            <Stack
              direction="row"
              alignItems="center"
              sx={{
                gap: { xs: 0.4, sm: 0.75 },
                flexShrink: 0,
              }}
            >
              {giftButton}
              {commentButton}
            </Stack>
          </Stack>
        </Box>
      </Box>
      <Dialog
        open={confirmDeleteId !== null}
        onClose={() => !isDeletingComment && setConfirmDeleteId(null)}
        PaperProps={{
          sx: {
            borderRadius: 3,
            background: panelBg,
            border: "1px solid",
            borderColor,
            color: darkMode ? "#fef3c7" : "#78350f",
            maxWidth: 440,
            width: "100%",
          },
        }}
      >
        <DialogTitle
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 1.25,
            fontWeight: 800,
            fontSize: "1.1rem",
            color: darkMode ? "#fca5a5" : "#b91c1c",
            pb: 1,
          }}
        >
          <WarningAmberRoundedIcon />
          Delete message?
        </DialogTitle>
        <DialogContent sx={{ color: textMuted, fontSize: "0.9rem", lineHeight: 1.55 }}>
          This message will be permanently removed from the Fan Wall. This action cannot be undone.
        </DialogContent>
        <DialogActions sx={{ p: 2, gap: 1 }}>
          <Button
            onClick={() => setConfirmDeleteId(null)}
            disabled={isDeletingComment}
            variant="text"
            sx={{
              textTransform: "none",
              color: chipColor,
              "&:hover": {
                backgroundColor: darkMode ? "rgba(245,158,11,0.10)" : "rgba(245,158,11,0.08)",
              },
            }}
          >
            Cancel
          </Button>
          <Button
            onClick={handleConfirmDelete}
            disabled={isDeletingComment}
            variant="contained"
            startIcon={
              isDeletingComment ? (
                <CircularProgress size={16} sx={{ color: "inherit" }} />
              ) : (
                <DeleteOutlineIcon />
              )
            }
            sx={{
              textTransform: "none",
              fontWeight: 700,
              px: 2,
              py: 0.75,
              borderRadius: 999,
              background: "linear-gradient(135deg, #ef4444 0%, #b91c1c 100%)",
              boxShadow: "0 8px 24px rgba(220,38,38,0.32)",
              "&:hover": {
                background: "linear-gradient(135deg, #f87171 0%, #991b1b 100%)",
              },
            }}
          >
            {isDeletingComment ? "Deleting…" : "Delete"}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CardExpInteraction;
